import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { mediaQueries } from 'shared/styles';

import { BackgroundField, backgroundStyle, BACKGROUND_AI_SCHEMA, DEFAULT_BACKGROUND } from '../fields';
import type { BackgroundValue } from '../fields';
import { altTextField, resolveAltText } from '../fields/widgets/alt-text-field';
import type { AltTextValue } from '../fields/widgets/alt-text-field';
import { AssetPickerField } from '../fields/widgets/asset-picker-field';
import { ContainerTextColorField } from '../fields/widgets/container-text-color-field';
import { selectField } from '../fields/widgets/select-field';
import { containerColorStyle, CONTAINER_TEXT_COLOR_AI_SCHEMA, INHERIT_COLOR } from '../fields/container-colors';
import { createSpacingFields } from '../fields/spacing-fields';
import { buildImgixDelivery, isImgixHostedSrc } from '../image-src';
import { BLOCK_SPACING, IMAGE_ASPECT_RATIOS, lookup, MAX_WIDTHS, NO_SPACING, spacingToCss } from '../fields/tokens';
import type { BoxSpacing, ImageAspectRatio, MaxWidthOption } from '../fields/tokens';
import { imgixSizing } from './image-style';
import { isSafeSrc } from './image';
import type { SlotProps, StarterChild } from './slot';
import { starterChild } from './slot';

/**
 * An image beside authored content, stacking vertically on mobile.
 *
 * **Deliberately a thin block rather than a second FlexLayout.** `FlexLayout` can already express a
 * text-and-image split (direction Horizontal, itemSizing Equal width, mobileStack Stack
 * vertically, with text and an Image in its slot), so this block adds no new capability. It exists
 * because "assemble a flex row from sixteen controls" is a developer's answer, not an author's:
 * a labelled Split Banner is the one obvious way to do a common case, while FlexLayout stays
 * available for the general one.
 *
 * That is also why its field set is narrow. Mirroring FlexLayout's gap/alignItems/justifyContent/
 * minHeight/border/itemSizing/wrap vocabulary would give an author two names for one idea, and
 * guarantee drift the first time one block's spacing scale changed and the other's did not. The
 * fields here are the decision that actually distinguishes a split banner from a flex row: which
 * side the image sits on, and the content beside it.
 *
 * **It stores no author text.** Content comes from a constrained slot allowing Heading, Text
 * (internal key `Paragraph`), Button and Image, exactly as `hero.tsx` does. That is load-bearing rather than stylistic: a block
 * holding its own author text would need its own sanitiser, hostile-input suite, and richtext
 * migration. Delegating means it already inherits Text's conversion and will inherit Heading's
 * separate follow-up without adding another text path.
 */

type ImagePosition = 'left' | 'right';

export type SplitBannerProps = {
  content?: React.FC<SlotProps>;
  imageSrc: string;
  imageAlt: AltTextValue;
  imagePosition: ImagePosition;
  imageAspectRatio: ImageAspectRatio;
  background: BackgroundValue;
  textColor: string;
  gap: string;
  maxWidth: MaxWidthOption;
  padding: BoxSpacing;
};

const IMAGE_POSITION_OPTIONS: { label: string; value: ImagePosition }[] = [
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
];

const ASPECT_RATIO_OPTIONS: { label: string; value: ImageAspectRatio }[] = [
  { label: 'Original', value: 'original' },
  { label: 'Square (1:1)', value: 'square' },
  { label: 'Landscape (4:3)', value: 'landscape' },
  { label: 'Wide (16:9)', value: 'wide' },
  { label: 'Portrait (3:4)', value: 'portrait' },
];

const GAP_OPTIONS = Object.keys(BLOCK_SPACING).map((key) => ({ label: key, value: key }));

const MAX_WIDTH_OPTIONS: { label: string; value: MaxWidthOption }[] = [
  { label: 'Narrow', value: 'narrow' },
  { label: 'Normal', value: 'normal' },
  { label: 'None', value: 'none' },
];

/**
 * Starter content, so a freshly dropped block reads as a split banner rather than an empty box with
 * an image on one side. Mirrors Hero's starter children; `id` is intentionally absent because
 * `populateIds()` stamps one and a hardcoded id would duplicate across instances.
 */
const SPLIT_BANNER_STARTER_CHILDREN: StarterChild[] = [
  starterChild('Heading', { text: '<p>A headline beside your image</p>', level: 'h2' }),
  starterChild('Paragraph', { text: '<p>Add a short description that supports the image.</p>' }),
];

/**
 * `row-reverse` rather than reordering the DOM: the image element stays FIRST in source order
 * regardless of which side it renders on, so a screen reader and a keyboard user meet the image and
 * the content in a stable order while the visual arrangement changes.
 *
 * Direction is a custom property read by the stylesheet rather than an inline style, because an
 * inline `flex-direction` cannot be overridden by a media query without `!important`. Same
 * mechanism `flex-layout.tsx` and `spacer.tsx` already use -- deliberately reused rather than
 * inventing a third.
 *
 * The mobile stack is not a separate control. A left/right split has to become a vertical stack on
 * a phone, and an author would not think to configure that; making it automatic is the whole point
 * of offering a preconfigured block.
 */
export const StyledSplitBanner = styled.div`
  display: flex;
  flex-direction: var(--cms-split-banner-direction, row);
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  gap: var(--cms-split-banner-gap, 0px);
  max-width: var(--cms-split-banner-max-width, none);
  margin-inline: auto;
  padding: var(--cms-split-banner-padding, 0px);

  & > * {
    flex: 1 1 0;
    min-width: 0;
  }

  @media ${mediaQueries.largePhone} {
    flex-direction: column;
  }
`;

// No align-self override: `StyledSplitBanner`'s `align-items: center` is left in place, so this
// side sizes to its own content (the image's aspect-ratio-driven height) rather than stretching to
// match the content side's height. `align-self: stretch` here used to force a definite height on
// this box, which SplitImage's `height: 100%` then inherited -- making `aspect-ratio` a no-op per
// the CSS spec (it only affects sizing when at least one dimension is auto). Found in review of
// ECOMM-13237: selecting Square/Wide/Portrait/Landscape in the editor changed nothing visually.
const ImageSide = styled.div``;

// `height` is deliberately UNSET, not 100%, mirroring `image.tsx`'s StyledImage (the same field on
// a different block): only `width` is fixed, so `aspect-ratio` is free to compute `height` from it.
// Setting both dimensions makes aspect-ratio inert -- see the note on ImageSide above.
const SplitImage = styled.img`
  display: block;
  width: 100%;
  object-fit: cover;
  aspect-ratio: var(--cms-split-banner-aspect-ratio, auto);
`;

const ImagePlaceholderBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 160px;
  border: 1px dashed var(--dcm-cms-placeholder-border-color, #c2c9cd);
  border-radius: var(--dcm-cms-placeholder-radius, 8px);
  background: var(--dcm-cms-image-placeholder-bg, #f0f2f4);
  color: var(--dcm-cms-image-placeholder-color, #646d72);
  font-size: 14px;
`;

/** Saved page data is unvalidated JSON, so a stored src may be absent or a hostile scheme. */
function usableImageSrc(src: unknown): string {
  return typeof src === 'string' && src !== '' && isSafeSrc(src) ? src : '';
}

const SPACING_FIELDS = createSpacingFields();

export const splitBannerConfig: ComponentConfig<SplitBannerProps> = {
  label: 'Split Banner',
  ai: {
    instructions:
      'An image beside a short block of content, stacking vertically on mobile. Put the Heading and Text blocks in the content slot rather than describing the copy here. Use this for a single image-plus-copy pairing; use Flex Layout when a row needs more than two sides or custom alignment.',
  },
  fields: {
    content: { type: 'slot', allow: ['Heading', 'Paragraph', 'Button', 'Image'] },
    imageSrc: {
      type: 'custom',
      label: 'Image',
      render: AssetPickerField,
      ai: {
        schema: { type: 'string' },
        stream: false,
        instructions: 'Full image URL. Do not stream partial URLs.',
      },
    },
    imageAlt: altTextField('Image alt text'),
    imagePosition: selectField(
      'Image side',
      IMAGE_POSITION_OPTIONS,
      'Which side the image sits on for desktop widths.',
      'On phones the image always moves above the content.'
    ),
    imageAspectRatio: selectField(
      'Image shape',
      ASPECT_RATIO_OPTIONS,
      'The frame the image is cropped to. Original keeps the uploaded proportions.'
    ),
    background: {
      type: 'custom',
      label: 'Background',
      render: BackgroundField,
      ai: {
        schema: BACKGROUND_AI_SCHEMA as Record<string, unknown>,
        instructions:
          'Background colour or image behind the whole banner. Prefer a theme colour role so it follows merchant branding.',
      },
    },
    textColor: {
      type: 'custom',
      label: 'Text color',
      render: ContainerTextColorField,
      ai: {
        // The shared schema, as `container-colors.ts` requires of every ContainerTextColorField
        // consumer: the control offers named roles plus a hex escape hatch, so the AI plugin has to
        // be told a raw hex is legal or it emits values the control cannot represent.
        schema: (CONTAINER_TEXT_COLOR_AI_SCHEMA as unknown) as Record<string, unknown>,
        instructions:
          "Text colour for everything in the banner. Use 'inherit' unless the background needs a specific contrast.",
      },
    },
    gap: selectField('Space between', GAP_OPTIONS, 'Gap between the image and the content.'),
    maxWidth: selectField('Max width', MAX_WIDTH_OPTIONS, 'Constrains the banner width for readability.'),
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: SPACING_FIELDS,
    },
  },
  defaultProps: {
    content: (SPLIT_BANNER_STARTER_CHILDREN as unknown) as React.FC<SlotProps>,
    imageSrc: '',
    imageAlt: { alt: '', decorative: false },
    imagePosition: 'left',
    imageAspectRatio: 'landscape',
    background: DEFAULT_BACKGROUND,
    textColor: INHERIT_COLOR,
    gap: 'md',
    maxWidth: 'none',
    padding: NO_SPACING,
  },
  render: ({
    content: slotContent,
    imageSrc = '',
    imageAlt,
    imagePosition = 'left',
    imageAspectRatio = 'landscape',
    background,
    textColor = INHERIT_COLOR,
    gap = 'md',
    maxWidth = 'none',
    padding,
    puck,
  }) => {
    const src = usableImageSrc(imageSrc);
    const alt = resolveAltText(imageAlt);
    // `imgixSizing` only understands preset keys or a '<n>px' string, so a '50%' would silently fall
    // through to the fluid path and emit `sizes: 100vw` -- telling the browser to size for the whole
    // viewport for an image that occupies about half of it. `medium` is the honest preset here.
    const delivery = src && isImgixHostedSrc(src) ? buildImgixDelivery(src, imgixSizing('medium')) : undefined;
    const Slot = slotContent;

    return (
      <StyledSplitBanner
        data-testid='cms-split-banner'
        data-image-position={imagePosition}
        style={
          {
            ...backgroundStyle(background),
            ...containerColorStyle(textColor),
            // `row-reverse` keeps the image first in source order while moving it visually.
            '--cms-split-banner-direction': imagePosition === 'right' ? 'row-reverse' : 'row',
            '--cms-split-banner-gap': lookup(BLOCK_SPACING, gap, gap),
            '--cms-split-banner-max-width': lookup(MAX_WIDTHS, maxWidth, MAX_WIDTHS.none),
            '--cms-split-banner-padding': spacingToCss(padding),
            '--cms-split-banner-aspect-ratio': lookup(
              IMAGE_ASPECT_RATIOS,
              imageAspectRatio,
              IMAGE_ASPECT_RATIOS.original
            ),
          } as React.CSSProperties
        }
      >
        {/*
         * The image side is omitted entirely when there is no image on a published page, rather than
         * rendered empty. `StyledSplitBanner`'s `& > *` rule gives EVERY direct child `flex: 1 1 0`,
         * so an empty div still claims half the row -- an author who publishes before picking an
         * image would ship a conspicuous blank half beside their text. In the editor it stays, or the
         * author cannot tell the block has an image slot at all.
         */}
        {src || puck.isEditing ? (
          <ImageSide data-testid='cms-split-banner-image-side'>
            {src ? (
              <SplitImage
                src={delivery?.src ?? src}
                srcSet={delivery?.srcSet}
                sizes={delivery?.sizes}
                alt={alt.decorative ? '' : alt.alt}
                aria-hidden={alt.decorative ? true : undefined}
                loading='lazy'
              />
            ) : (
              <ImagePlaceholderBox>Pick an image in the settings panel.</ImagePlaceholderBox>
            )}
          </ImageSide>
        ) : null}
        {Slot ? <Slot /> : <div />}
      </StyledSplitBanner>
    );
  },
};
